import type { GetStaticPaths, GetStaticProps } from 'next'
import Link from 'next/link'
import { Blocks, CtaBar, Faq, RelatedLinks, Tldr } from '@/components/Seo/ContentBlocks'
import { SPORTS, Sport, STATS, findSport } from '@/lib/seoContent'
import { BRAND, ORG_ID, absUrl, breadcrumbLd, faqLd, SeoProps } from '@/utils/seo'

const SportPage = ({ sport }: { sport: Sport }) => (
  <div className="min-h-screen bg-gray-50">
    <div className="mx-auto max-w-4xl px-4 py-8 md:py-12">
      <nav aria-label="面包屑" className="mb-4 flex gap-1 text-sm text-gray-500">
        <Link href="/" className="text-gray-500 hover:text-gray-900">
          首页
        </Link>
        <span>/</span>
        <Link href="/guide" className="text-gray-500 hover:text-gray-900">
          按项目办赛
        </Link>
      </nav>
      <article className="flex flex-col gap-8 rounded-2xl bg-white p-6 shadow-sm ring-1 ring-black/5 md:p-10">
        <header className="flex flex-col gap-3">
          <h1 className="text-3xl font-black leading-tight text-gray-900">{sport.h1}</h1>
          <Tldr text={sport.tldr} />
          <div className="grid grid-cols-3 gap-3 text-center">
            {[
              [STATS.matches, '累计承办比赛'],
              [STATS.teams, '参赛队伍'],
              [STATS.users, '注册用户'],
            ].map(([v, l]) => (
              <div key={l} className="rounded-xl bg-gray-50 p-3">
                <div className="text-xl font-black text-[#DB3142]">{v}</div>
                <div className="text-xs text-gray-500">{l}</div>
              </div>
            ))}
          </div>
          <div className="text-xs text-gray-400">数据截至 {STATS.asOf}</div>
        </header>
        <section className="flex flex-col gap-3">
          <h2 className="text-xl font-bold text-gray-900">支持的{sport.name}赛制</h2>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {sport.formats.map((f) => (
              <div key={f.name} className="rounded-xl bg-gray-50 p-4">
                <h3 className="m-0 text-base font-semibold text-gray-900">{f.name}</h3>
                <p className="m-0 mt-1 text-sm text-gray-600">{f.desc}</p>
              </div>
            ))}
          </div>
        </section>
        <Blocks blocks={sport.blocks} />
        <Faq faq={sport.faq} />
        <CtaBar />
        <RelatedLinks links={sport.related} />
      </article>
    </div>
  </div>
)

export const getStaticPaths: GetStaticPaths = async () => ({
  paths: SPORTS.map((s) => ({ params: { sport: s.slug } })),
  fallback: false,
})

export const getStaticProps: GetStaticProps = async ({ params }) => {
  const sport = findSport(String(params?.sport))
  if (!sport) return { notFound: true }
  const path = `/sports/${sport.slug}`
  const seo: SeoProps = {
    title: sport.title,
    description: sport.description,
    keywords: sport.keywords,
    path,
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'SoftwareApplication',
        name: `灵缇赛事系统 · ${sport.name}`,
        applicationCategory: 'SportsApplication',
        operatingSystem: 'Web, 微信小程序, iOS, Android',
        url: absUrl(path),
        description: sport.description,
        offers: { '@type': 'Offer', price: '0', priceCurrency: 'CNY', description: '基础功能免费' },
        publisher: { '@id': ORG_ID, '@type': 'Organization', name: BRAND },
        featureList: sport.formats.map((f) => `${f.name}:${f.desc}`).join(';'),
      },
      faqLd(sport.faq),
      breadcrumbLd([
        ['首页', '/'],
        ['按项目办赛', '/guide'],
        [sport.h1, path],
      ]),
    ],
  }
  return { props: { sport, seo } }
}

export default SportPage
